import {
  DateTime,
  DateTimeFormatOptions,
  DateTimeOptions,
  DateTimeUnit,
  DurationLikeObject,
} from 'luxon'

import {ExactOrEstimatedDate, HealthDataResolution, DayOfWeek} from '../models'
import {isTruthy} from './helpers'

export const formatToJSDate = (date: string | null | undefined) => {
  if (!date) {
    return undefined
  }
  return DateTime.fromISO(date).toJSDate()
}

export const formatDate = (
  format: DateTimeFormatOptions,
  date: string | null | undefined,
  options?: DateTimeOptions,
  defaultValue = '',
) => {
  if (!date) {
    return defaultValue
  }
  return DateTime.fromISO(date, options).toLocaleString(format)
}

export const monthYearDateFormat: DateTimeFormatOptions = {
  month: 'long',
  year: 'numeric',
}

export const formatExactOrEstimatedDate = (
  date?: ExactOrEstimatedDate | null,
  options?: DateTimeOptions,
) =>
  date?.isEstimated
    ? formatDate(monthYearDateFormat, date.date, options)
    : formatDate(DateTime.DATE_FULL, date?.date, options)

export const createExactOrEstimatedDate = (
  date?: string | null,
  isEstimated = false,
) =>
  date
    ? {
        date,
        isEstimated,
      }
    : null

const getCalendarFormat = (date: DateTime) => {
  const diff = date.diff(DateTime.now().startOf('day'), 'days').as('days')

  if (diff < -6) {
    return 'sameElse'
  }
  if (diff < -1) {
    return 'lastWeek'
  }
  if (diff < 0) {
    return 'lastDay'
  }
  if (diff < 1) {
    return 'sameDay'
  }
  if (diff < 2) {
    return 'nextDay'
  }
  if (diff < 7) {
    return 'nextWeek'
  }
  return 'sameElse'
}

const calendarDateFormats = {
  sameDay: "'Today at' t",
  nextDay: "'Tomorrow at' t",
  nextWeek: "EEEE 'at' t",
  lastDay: "'Yesterday at' t",
  lastWeek: "'Last' EEEE 'at' t",
}

export const getCalendarDateTime = (
  date?: string | null,
  setZone?: boolean,
) => {
  if (!date) {
    return ''
  }

  const parsedDate = DateTime.fromISO(date, {
    setZone,
  })
  const format = getCalendarFormat(parsedDate)

  if (format === 'sameElse') {
    return parsedDate.toLocaleString(DateTime.DATETIME_MED)
  }
  return parsedDate.toFormat(calendarDateFormats[format])
}

export const resolutionUnitMap: Record<HealthDataResolution, DateTimeUnit> = {
  [HealthDataResolution.MONTH]: 'month',
  [HealthDataResolution.WEEK]: 'week',
  [HealthDataResolution.DAY]: 'day',
}

export const getResolutionDateDiff: (
  resolution: HealthDataResolution,
  intervalCount?: number,
) => DurationLikeObject = (resolution, intervalCount = 1) => ({
  [resolutionUnitMap[resolution]]: intervalCount,
})

export const getHealthDataIntervalEnd = (
  startDate: DateTime,
  resolution: HealthDataResolution,
  intervalCount?: number,
) =>
  startDate
    .plus(getResolutionDateDiff(resolution, intervalCount))
    .minus({day: 1})

export const getAlignedHealthDataIntervalStart = (
  endDate: DateTime,
  resolution: HealthDataResolution,
  intervalCount = 1,
) =>
  endDate
    .startOf(resolutionUnitMap[resolution])
    .minus(getResolutionDateDiff(resolution, intervalCount - 1)) // we substract 1, because one (incomplete) interval has already been substracted by startOf()

export const formatDateInterval = (
  startDate: DateTime,
  endDate: DateTime,
  dateFormat: Intl.DateTimeFormatOptions = DateTime.DATE_SHORT,
) => {
  if (startDate.startOf('day').toISO() === endDate.startOf('day').toISO()) {
    return startDate.toLocaleString(dateFormat)
  }

  return `${startDate.toLocaleString(dateFormat)} - ${endDate.toLocaleString(
    dateFormat,
  )}`
}

export const formatHealthDataDatePeriod = (
  startOfPeriod: string,
  resolution?: HealthDataResolution | null,
) => {
  if (!resolution || resolution === HealthDataResolution.DAY) {
    return formatDate(DateTime.DATE_SHORT, startOfPeriod)
  }

  const startDate = DateTime.fromISO(startOfPeriod)
  const endDate = getHealthDataIntervalEnd(startDate, resolution)

  return formatDateInterval(startDate, endDate)
}

export const getMinDate = (dates: (Date | null | undefined)[]) => {
  const filteredDates = dates.filter(isTruthy)

  if (!filteredDates.length) {
    return undefined
  }

  return filteredDates.reduce((minDate, currentDate) =>
    minDate < currentDate ? minDate : currentDate,
  )
}

export const getMaxDate = (dates: (Date | null | undefined)[]) => {
  const filteredDates = dates.filter(isTruthy)

  if (!filteredDates.length) {
    return undefined
  }

  return filteredDates.reduce((maxDate, currentDate) =>
    maxDate > currentDate ? maxDate : currentDate,
  )
}

export const getTodayAsString = () => DateTime.now().toISODate().split('T')[0]

// --- Local calendar date helpers (YYYY-MM-DD without timezone shifts) ---
// These helpers ensure we never convert through UTC, avoiding off-by-one issues
// in negative time zones when users pick a calendar date.
export function toLocalYyyyMmDd(date?: Date | null): string {
  if (!date) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function fromYyyyMmDdLocal(dateStr?: string | null): Date | null {
  if (!dateStr) return null
  const parts = dateStr.split('-')
  if (parts.length !== 3) return null
  const year = parseInt(parts[0], 10)
  const month = parseInt(parts[1], 10)
  const day = parseInt(parts[2], 10)
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day)
  )
    return null
  if (month < 1 || month > 12) return null
  if (day < 1 || day > 31) return null
  return new Date(year, month - 1, day)
}

export const MINIMUM_PATIENT_AGE = 16

export const getMinimumDateOfBirthForAge = (minimumAge: number) => {
  const date = new Date()
  date.setFullYear(date.getFullYear() - minimumAge)
  return toLocalYyyyMmDd(date)
}

export const isAtLeastMinimumAge = (
  dateOfBirth: string,
  minimumAge: number,
) => {
  if (!dateOfBirth) return false
  const parsed = new Date(`${dateOfBirth}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return false
  return dateOfBirth <= getMinimumDateOfBirthForAge(minimumAge)
}

// --- Web datetime helpers migrated from web/src/utils/datetime.ts ---

export function formatDay(day: DayOfWeek): string {
  const lower = day.toLowerCase()
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

export function formatLocalTime(
  time?: {hour?: number; minute?: number} | string | null,
): string {
  if (!time) return '-'
  if (typeof time === 'string') {
    const parts = time.split(':')
    if (parts.length >= 2) {
      const [hh, mm] = parts
      return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}`
    }
    const parsed = DateTime.fromISO(time)
    return parsed.isValid ? parsed.toFormat('HH:mm') : '-'
  }
  if (time.hour == null || time.minute == null) return '-'
  return DateTime.fromObject({hour: time.hour, minute: time.minute}).toFormat(
    'HH:mm',
  )
}

export function formatLocalDateTime(value?: string | null): string {
  if (!value) return '-'
  const parsed = DateTime.fromISO(value)
  return parsed.isValid ? parsed.toFormat('yyyy-LL-dd HH:mm') : '-'
}

export function toInputDateTimeLocal(value?: string | null): string {
  if (!value) return ''
  const dt = DateTime.fromISO(value)
  if (!dt.isValid) return ''
  return dt.toFormat("yyyy-LL-dd'T'HH:mm")
}

export function ensureSecondsInDateTimeLocal(value: string): string {
  // Convert a datetime-local (no timezone) into ISO UTC with 'Z'
  // Accepts: yyyy-MM-ddTHH:mm or yyyy-MM-ddTHH:mm:ss
  if (!value) return ''
  let dt = DateTime.fromISO(value)
  if (!dt.isValid) return ''
  // If original had no seconds, make sure seconds are set (0 by default)
  if (value.length === 16) {
    dt = dt.set({second: 0})
  }
  return dt.toUTC().toISO() || ''
}

export function toTimeString(
  time?: {hour?: number; minute?: number} | string | null,
): string {
  if (!time) return ''
  if (typeof time === 'string') {
    const parts = time.split(':')
    if (parts.length >= 2) {
      const [hh, mm] = parts
      return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}`
    }
    return ''
  }
  const {hour, minute} = time
  if (hour == null || minute == null) return ''
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

export function withSeconds(time: string): string {
  // Input expected from <input type="time"> usually HH:mm; ensure HH:mm:ss
  if (!time) return ''
  const parts = time.split(':')
  if (parts.length === 2) {
    const [hh, mm] = parts
    return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}:00`
  }
  if (parts.length >= 3) {
    const [hh, mm, ss] = parts
    return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}:${(
      ss || '00'
    ).padStart(2, '0')}`
  }
  return time
}

// --- Validation helpers for time and datetime-local inputs ---

export function isValidTimeHHmm(value?: string | null): boolean {
  if (!value) return false
  const match = /^\d{2}:\d{2}$/.exec(value)
  if (!match) return false
  const [hh, mm] = value.split(':')
  const hours = parseInt(hh || '0', 10)
  const minutes = parseInt(mm || '0', 10)
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return false
  if (hours < 0 || hours > 23) return false
  if (minutes < 0 || minutes > 59) return false
  return true
}

export function isTimeOrderValid(start: string, end: string): boolean {
  if (!isValidTimeHHmm(start) || !isValidTimeHHmm(end)) return false
  // HH:mm string compare works, but compare numerically for clarity
  const [sh, sm] = start.split(':').map((v) => parseInt(v, 10))
  const [eh, em] = end.split(':').map((v) => parseInt(v, 10))
  if (sh < eh) return true
  if (sh > eh) return false
  return sm < em
}

export function isValidDateTimeLocal(value?: string | null): boolean {
  if (!value) return false
  // Accept shapes like yyyy-MM-ddTHH:mm or yyyy-MM-ddTHH:mm:ss
  const shapeOk = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2})?$/.test(value)
  if (!shapeOk) return false
  const dt = DateTime.fromISO(value)
  return dt.isValid
}

// Range inclusion helpers (HH:mm)
export type HhMmRange = {start: string; end: string}

export function isRangeInside(
  start: string,
  end: string,
  allowStart: string,
  allowEnd: string,
): boolean {
  if (!isValidTimeHHmm(start) || !isValidTimeHHmm(end)) return false
  if (!isValidTimeHHmm(allowStart) || !isValidTimeHHmm(allowEnd)) return false
  if (!isTimeOrderValid(start, end)) return false
  if (!isTimeOrderValid(allowStart, allowEnd)) return false
  // start >= allowStart AND end <= allowEnd
  return !(start < allowStart) && !(end > allowEnd)
}

export function isRangeInsideAny(
  start: string,
  end: string,
  allowed: HhMmRange[],
): boolean {
  if (!allowed || allowed.length === 0) return false
  return allowed.some((r) => isRangeInside(start, end, r.start, r.end))
}

// Simple HH:mm overlap check for already validated and ordered ranges
// Overlap rule on half-open intervals: [aStart, aEnd) and [bStart, bEnd) overlap if aStart < bEnd && bStart < aEnd
export function doHhMmRangesOverlap(
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string,
): boolean {
  return aStart < bEnd && bStart < aEnd
}

// --- Bookings portal helpers ---
// Formats a legend like "Wednesday 05 November - 09:30" from date string yyyy/MM/dd and optional HH:mm
export function formatDateLegend(dateStr?: string, timeStr?: string): string {
  if (!dateStr) return ''
  const parts = dateStr.split('/')
  if (parts.length === 3) {
    const yyyy = Number(parts[0])
    const mm = Number(parts[1])
    const dd = Number(parts[2])
    if (!Number.isNaN(yyyy) && !Number.isNaN(mm) && !Number.isNaN(dd)) {
      const d = new Date(yyyy, mm - 1, dd)
      const weekday = d.toLocaleDateString(undefined, {weekday: 'long'})
      const dayStr = String(d.getDate()).padStart(2, '0')
      const monthLong = d.toLocaleDateString(undefined, {month: 'long'})
      return `${weekday} ${dayStr} ${monthLong}${
        timeStr ? ` - ${timeStr}` : ''
      }`
    }
  }
  return `${dateStr}${timeStr ? ` - ${timeStr}` : ''}`
}

export function getTimeZoneNotice(
  timeZoneId: string | undefined,
  t: (key: string, options?: {timeZone?: string}) => string,
): string {
  if (!timeZoneId) return ''
  return t('All times are in {{timeZone}}', {timeZone: timeZoneId})
}

// Reusable datetime helpers extracted from AddAppointmentCard
export function formatDateTimeLocal(date: Date): string {
  const pad = (num: number): string => num.toString().padStart(2, '0')
  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1)
  const day = pad(date.getDate())
  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())

  return `${year}-${month}-${day}T${hours}:${minutes}`
}

export function parseDateTimeLocal(datetimeString: string): Date {
  const [datePart, timePart] = datetimeString.split('T')
  const [year, month, day] = datePart.split('-').map(Number)
  const [hours, minutes] = timePart.split(':').map(Number)
  return new Date(year, month - 1, day, hours, minutes)
}

export function parseSlotDateTime(dateStr: string, timeStr: string): Date {
  const [year, month, day] = dateStr.split('/').map(Number)
  const [hours, minutes] = timeStr.split(':').map(Number)
  return new Date(year, month - 1, day, hours, minutes)
}

export function parseSlashDateToDateTime(
  dateStr?: string | null,
): DateTime | null {
  if (!dateStr) return null
  const [year, month, day] = dateStr.split('/').map(Number)
  if (!year || !month || !day) return null
  return DateTime.local(year, month, day).startOf('day')
}
